import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const FAQ_ITEMS = [
  {
    question: 'How long does it take to receive my custom cushion?',
    answer: 'Most orders are completed within 10-15 business days from the date of order confirmation. Complex orders or large quantities may take slightly longer. We will notify you if there are any delays.',
  },
  {
    question: 'What types of foam do you offer?',
    answer: 'We offer four premium Australian-made foam grades ranging from soft (17-070) to firm (35-200). Each grade is suited to different applications — from plush back cushions to firm dining seats. Visit our "Our Foams" page for detailed descriptions.',
  },
  {
    question: 'Can I order fabric samples before placing my cushion order?',
    answer: 'Absolutely! We offer up to 10 free fabric samples per order. Visit our Fabric Samples page to browse and select your favourites. Samples are posted within 2-3 business days.',
  },
  {
    question: 'How do I measure my cushion correctly?',
    answer: 'Measure the length, width, and depth of the area where the cushion will sit. For replacement cushions, measure the existing cushion or the furniture frame. Always measure in centimetres and include any curves or angles. If in doubt, contact us for guidance.',
  },
  {
    question: 'What is your return and refund policy?',
    answer: 'Because all our cushions are custom-made to your specifications, we cannot accept returns unless the product is faulty or does not match your order. If you receive a defective product, please contact us within 7 days of delivery with photos and we will arrange a replacement or refund.',
  },
  {
    question: 'Do you offer trade or wholesale pricing?',
    answer: 'Yes! We offer trade pricing for upholsterers, interior designers, furniture makers, and other trade professionals. Visit our Trade page to learn more about our trade program and how to apply.',
  },
  {
    question: 'Can I use my own fabric?',
    answer: 'Currently, we offer a curated selection of high-quality fabrics from leading Australian suppliers. If you have a specific fabric in mind, please contact us to discuss whether we can accommodate your request.',
  },
  {
    question: 'Do you deliver Australia-wide?',
    answer: 'Yes, we deliver across Australia. Shipping costs are calculated at checkout based on your location and order size. Metro areas typically receive delivery within 3-5 business days after dispatch.',
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-b border-gray-100 last:border-0" data-testid="faq-item">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 text-left group"
      data-testid="faq-toggle"
    >
      <span className="text-base sm:text-lg font-medium text-[#1A1A1A] pr-4 group-hover:text-teal-700 transition-colors">{question}</span>
      <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}>
      <p className="text-gray-600 leading-relaxed">{answer}</p>
    </div>
  </div>
);

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-[#FAFAF8]" data-testid="faq-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Frequently Asked Questions</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">
            Find answers to common questions about our custom cushions, materials, ordering, and delivery.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-6 sm:py-12">
        <div className="bg-white rounded-2xl px-6 sm:px-8 border border-gray-100 shadow-sm divide-y-0">
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem
              key={i}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
